import { forEach, reverse, sortBy } from 'lodash';
import { useTranslation } from '@lib/useTypedTranslation';
import { Link } from 'react-router-dom';
import { useWorldRoutes } from '../../../routes/parts/allWorldRoutes';
import './alerts-overview.css';
import { SmartBatteryAlertStatus } from '../../../services/config/alertConstants';
import { useWorldSettingsContext } from '../../../context/worldSettings';
import { isSmartBatteryAlertEnabled } from '../../../services/config/config';

import { SmartBatteryAlertLevel } from '../../../services/shared/battery/batteryCentricService';
import { getPercentage, typedEntries } from './common';
import { ReactElement, useMemo } from 'react';
import { AlertsTable } from '@components/alertsTable/alertsTable';


export interface SortedAlert {
  name: SmartBatteryAlertStatus,
  red: number,
  yellow: number,
  sum: number,
  text: ReactElement | string
}


type BatteryAlerts = Omit<Record<SmartBatteryAlertStatus, SmartBatteryAlertLevel>, 'all'> & {
  all: number
};

interface IProps {
  alerts: BatteryAlerts
}

export type BatteryAlertText = {
  [alert in SmartBatteryAlertStatus]: string
};

export function AlertsOverview(props: IProps) {
  const { alerts } = props;

  const ns1 = 'batteryOverview';
  const ns2 = 'translation';
  const { t } = useTranslation([ns1, ns2]);
  const { worldSettings } = useWorldSettingsContext();
  const basePath = useWorldRoutes().batteryEssentials.battery;

  const sortAlerts = useMemo(() => {
    if (!alerts) { return []; }
    const sortedAlerts: SortedAlert[] = [];
    const alertsText: BatteryAlertText = {
      batteryHealthStatus: t('HEALTH_ALERT', { ns: ns1 }),
      batteryAverageDischargeStatus: t('AVERAGE_DISCHARGE_ALERT', { ns: ns1 }),
      batteryCycleCountStatus: t('CYCLE_COUNT_ALERT', { ns: ns1 })
    };
    for (const [key, alertData] of typedEntries(alerts)) {
      if (key === 'all') {
        // do not attempt to create an entry for 'all'
        continue;
      }
      const { red, yellow } = alertData as SmartBatteryAlertLevel;
      const sum = red + yellow;
      const enabledAlert = isSmartBatteryAlertEnabled(key, worldSettings);
      if (sum && enabledAlert) {
        const item: SortedAlert = {
          name: key,
          red,
          yellow,
          sum,
          text: ''
        };
        const percentage = item.sum === 1 ? '1' : getPercentage(item.sum, alerts.all);
        const ofText = item.sum === 1 ? "" : t("OF", { ns: ns2 });
        const batteryText = t("BATTERY", { count: item.sum, ns: ns2 });
        const hasText = t("HAS", { count: item.sum, ns: ns1 });
        const sumLink = `${basePath}?alertStatus=${key}-red,${key}-yellow`;
        const sumDisplay = <>(<Link to={sumLink} className="alerts__link">{sum}</Link>)</>;
        item.text = (
          <>
            <b>{percentage}</b> {ofText} {batteryText} {sumDisplay} {hasText} {alertsText[key as SmartBatteryAlertStatus]}
          </>
        );
        sortedAlerts.push(item);
      }
    }
    return reverse(sortBy(sortedAlerts, 'sum'));
  }, [alerts, basePath, t, worldSettings]);

  return (
    <div className="battery-overview-alerts">
      <div className="battery-overview-alerts__container">
        <AlertsTable alerts={sortAlerts} />
      </div>
    </div>
  );
}
